import React from 'react'
import classNames from 'classnames'
import dynamic from 'next/dynamic'
import { When } from 'react-if'
import { LazyHydrate } from '@/atoms/LazyHydrate'
import { TitleMD } from '@/atoms/Text'
import { slugs } from '@/constants/slugs'
import { contentfulFaqLocations, useProjectFaqs } from '@/services/FaqService'
import { isGuildSignupSimpleLayout } from '@/services/GuildSignupPageService/validations'
import { useLocale } from '@/utils/LocaleUtil'
import { useTranslate } from '@/utils/translate/translate-client'
import { GuildEmailInput } from '../GuildEmailInput'
import { useGuildSignupContext } from '../GuildSignupContext'

// Dynamic imports for below-the-fold components
const AppPromoSection = dynamic(
  () => import('@/views/LandingView/AppPromoSection').then((mod) => mod.AppPromoSection),
  {
    ssr: false,
  },
)
const FaqAccordion = dynamic(() => import('@/organisms/FaqAccordion').then((mod) => mod.FaqAccordion), {
  ssr: false,
})

interface GuildBodySectionProps {
  className?: string
  hasUser: boolean
  canShowSimpleSalePage?: boolean
}

export const GuildBodySection: React.FC<GuildBodySectionProps> = ({
  className,
  hasUser,
  canShowSimpleSalePage = false,
}) => {
  const { locale } = useLocale()
  const { layout, isHideAppPromo, isHideJoinTheMovement } = useGuildSignupContext()
  const { t } = useTranslate('join-the-guild')
  const isSimple = isGuildSignupSimpleLayout(layout)

  const { faqs: guildFaqs, loadingProjectFaqs } = useProjectFaqs({
    locale,
    preview: false,
    projectSlug: slugs.angelStudios,
    location: contentfulFaqLocations.guild,
  })

  return (
    <div
      className={classNames(
        'flex flex-col items-center text-center overflow-x-hidden',
        className,
        isSimple ? 'bg-core-gray-200 text-black' : 'bg-core-gray-950 text-white',
      )}
    >
      <When condition={!isHideAppPromo}>
        <AppPromoSection containerClassName="h-full pt-10 !pb-0" simplified />
      </When>

      <When condition={!isHideJoinTheMovement && !canShowSimpleSalePage}>
        <LazyHydrate whenVisible>
          <>
            <section
              aria-label={t('joinTheMovement', 'Join the movement.')}
              id="plan-selection"
              className="flex w-full flex-col items-center bg-core-gray-100 px-4 py-[104px] text-center text-core-gray-950"
            >
              <TitleMD
                weight="bold"
                className="md:photon-heading-sm lg:photon-heading-xl max-w-[700px] pb-10 md:pb-6 lg:pb-0"
              >
                {t(
                  'joinMovementWatchSupportEntertainment',
                  'Join the Movement to watch and support entertainment that Amplifies Light',
                )}
              </TitleMD>
              <LazyHydrate whenVisible>
                <GuildEmailInput hasUser={hasUser} isHero={false} />
              </LazyHydrate>
            </section>
          </>
        </LazyHydrate>
      </When>
      <When condition={(guildFaqs?.length || 0) > 0}>
        <section
          aria-label={t('frequentlyAskedQuestions', 'Frequently Asked Questions')}
          id="faqs"
          key="faqs"
          className={classNames(
            'w-full mx-auto scroll-mt-[73px] px-8 py-16 text-left md:max-w-[800px]',
            isSimple ? 'text-black' : 'text-white',
          )}
        >
          {!loadingProjectFaqs && <FaqAccordion faqs={guildFaqs} showTopBorder={true} />}
        </section>

        <div className="mb-8 flex min-h-[72px] w-full max-w-[600px] items-center justify-center">
          <GuildEmailInput hasUser={hasUser} isHero={true} />
        </div>
      </When>
    </div>
  )
}
